import { FormSection } from '../FormSection';
import { useCallback, useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Columns } from './ImportFileForm';
import { Dropdown, OptionsType } from '../core/Dropdown';
import { FormElement } from '../FormElement';
import { useFieldMappings, FieldMappingOption } from '@/hooks/useFieldMappings';
import { useRememberedImportMappings } from '@/hooks/useRememberedImportMappings';
import { useImportFileStore } from '@/stores/importFile';
import { cn } from '@/helpers/classnames';
import { StudentFormType } from '@/hooks/useStudentFormSchema';
import Button from '@/components/core/Button';

interface ImportColumnMapperProps {
  type: StudentFormType;
}

export type MappedColumns = {
  [key: string]: string;
};

export const ImportColumnMapper = ({ type }: ImportColumnMapperProps) => {
  const { import: importMeta, update: updateFileData } = useImportFileStore();
  const [mappedColumns, setMappedColumns] = useState<MappedColumns>(
    () => importMeta.mappedColumns ?? {},
  );
  const [suggestedHeaders, setSuggestedHeaders] = useState<Set<string>>(
    () => new Set(),
  );
  const hasAutoMatched = useRef(Boolean(importMeta.mappedColumns));
  const importMetaRef = useRef(importMeta);
  importMetaRef.current = importMeta;

  const { formatMessage } = useIntl();

  const {
    mappings,
    toDropdownOptions,
    findMatchingField,
    getRequiredFields,
    DO_NOT_USE_ID,
  } = useFieldMappings(type);
  const { findRememberedField } = useRememberedImportMappings(type);

  const createIndexedKey = useCallback((baseKey: string, index: number) => {
    return `${baseKey}_${index}`;
  }, []);

  const validateRequiredFields = useCallback(
    (columns: MappedColumns) => {
      const requiredApiFields = getRequiredFields.map((f) => f.apiField);

      const mappedApiFields = Object.values(columns).filter(
        (value) => !value.startsWith(DO_NOT_USE_ID),
      );

      const allRequiredMapped = requiredApiFields.every((field) =>
        mappedApiFields.includes(field),
      );

      const numberOfImportedColumns =
        importMetaRef.current.file?.importColumns?.filter(
          (col) => col.header.length > 0,
        ).length;

      const allFieldsSelected =
        Object.keys(columns).length === numberOfImportedColumns;

      return allRequiredMapped && allFieldsSelected;
    },
    [getRequiredFields, DO_NOT_USE_ID],
  );

  const changeOption = useCallback(
    (columnHeader: string, apiField: string) => {
      setSuggestedHeaders((prev) => {
        const next = new Set(prev);
        next.delete(columnHeader);
        return next;
      });
      setMappedColumns((prev) => {
        const newMappedColumns = { ...prev };

        if (prev[columnHeader] === apiField) {
          delete newMappedColumns[columnHeader];
          updateFileData({
            mappedColumns: newMappedColumns,
            status: {
              isAllColumnsValidated: validateRequiredFields(newMappedColumns),
            },
          });
          return newMappedColumns;
        }

        for (const key of Object.keys(newMappedColumns)) {
          if (
            newMappedColumns[key] === apiField &&
            !apiField.startsWith(DO_NOT_USE_ID)
          ) {
            delete newMappedColumns[key];
          }
        }

        newMappedColumns[columnHeader] = apiField;

        updateFileData({
          mappedColumns: newMappedColumns,
          status: {
            isAllColumnsValidated: validateRequiredFields(newMappedColumns),
          },
        });

        return newMappedColumns;
      });
    },
    [updateFileData, validateRequiredFields, DO_NOT_USE_ID],
  );

  const getFieldOptionWithMappingStatus = useCallback(
    (fieldOption: FieldMappingOption): OptionsType => {
      const isMapped = Object.values(mappedColumns).includes(
        fieldOption.value as string,
      );

      const usedByColumnHeader = isMapped
        ? Object.keys(mappedColumns).find(
            (key) => mappedColumns[key] === fieldOption.value,
          )
        : undefined;

      const descriptionParts: string[] = [];

      if (fieldOption.required) {
        descriptionParts.push(
          formatMessage({ defaultMessage: 'Obligatorisk' }),
        );
      }

      if (usedByColumnHeader) {
        descriptionParts.push(
          formatMessage(
            { defaultMessage: 'Används av: {column}' },
            { column: usedByColumnHeader },
          ),
        );
      }

      return {
        ...fieldOption,
        color: isMapped ? 'gray' : 'default',
        description:
          descriptionParts.length > 0
            ? descriptionParts.join(' · ')
            : undefined,
      } as OptionsType;
    },
    [mappedColumns, formatMessage],
  );

  const checkAndSetDropdownValue = useCallback(
    (field: Columns): OptionsType | undefined => {
      const apiField = mappedColumns[field.header];

      if (!apiField) return undefined;

      const fieldOption = toDropdownOptions.find((f) => f.value === apiField);

      if (!fieldOption) {
        return getFieldOptionWithMappingStatus({
          id: apiField,
          label: formatMessage({ defaultMessage: 'Använd inte' }),
          value: apiField,
        });
      }

      return getFieldOptionWithMappingStatus(fieldOption);
    },
    [
      mappedColumns,
      toDropdownOptions,
      getFieldOptionWithMappingStatus,
      formatMessage,
    ],
  );

  const handleSetUnassignedToDoNotUse = useCallback(() => {
    const columns = importMetaRef.current.file?.importColumns;
    if (!columns) return;

    setMappedColumns((prev) => {
      const newMappedColumns = { ...prev };

      columns.forEach((col, index) => {
        if (!newMappedColumns[col.header]) {
          newMappedColumns[col.header] = createIndexedKey(DO_NOT_USE_ID, index);
        }
      });

      updateFileData({
        mappedColumns: newMappedColumns,
        status: {
          isAllColumnsValidated: validateRequiredFields(newMappedColumns),
        },
      });

      return newMappedColumns;
    });
  }, [createIndexedKey, DO_NOT_USE_ID, updateFileData, validateRequiredFields]);

  useEffect(() => {
    if (
      hasAutoMatched.current ||
      !importMeta.file?.importColumns ||
      importMeta.file.importColumns.length === 0
    ) {
      return;
    }

    hasAutoMatched.current = true;

    const columns = importMeta.file.importColumns;
    const autoMappedColumns: MappedColumns = {};
    const rememberedHeaders = new Set<string>();
    const usedApiFields = new Set<string>();

    columns.forEach((col, index) => {
      const rememberedField = findRememberedField(col.header);
      if (rememberedField === DO_NOT_USE_ID) {
        autoMappedColumns[col.header] = createIndexedKey(DO_NOT_USE_ID, index);
        rememberedHeaders.add(col.header);
      } else if (
        rememberedField &&
        !usedApiFields.has(rememberedField) &&
        mappings.some((m) => m.apiField === rememberedField)
      ) {
        autoMappedColumns[col.header] = rememberedField;
        usedApiFields.add(rememberedField);
        if (findMatchingField(col.header)?.apiField !== rememberedField) {
          rememberedHeaders.add(col.header);
        }
      }
    });

    columns.forEach((col) => {
      if (autoMappedColumns[col.header]) return;
      const matchingField = findMatchingField(col.header);
      if (matchingField && !usedApiFields.has(matchingField.apiField)) {
        autoMappedColumns[col.header] = matchingField.apiField;
        usedApiFields.add(matchingField.apiField);
      }
    });

    if (Object.keys(autoMappedColumns).length > 0) {
      setMappedColumns(autoMappedColumns);
      setSuggestedHeaders(rememberedHeaders);
      updateFileData({
        mappedColumns: autoMappedColumns,
        status: {
          isAllColumnsValidated: validateRequiredFields(autoMappedColumns),
        },
      });
    }
  }, [
    importMeta.file?.importColumns,
    mappings,
    findMatchingField,
    findRememberedField,
    createIndexedKey,
    DO_NOT_USE_ID,
    updateFileData,
    validateRequiredFields,
  ]);

  if (
    !importMeta.file?.importColumns ||
    importMeta.file?.importColumns.length === 0 ||
    !toDropdownOptions
  )
    return null;

  const requiredApiFields = getRequiredFields.map((f) => f.apiField);
  const mappedApiFields = Object.values(mappedColumns).filter(
    (value) => !value.startsWith(DO_NOT_USE_ID),
  );
  const unmappedRequiredCount = requiredApiFields.filter(
    (field) => !mappedApiFields.includes(field),
  ).length;

  return (
    <FormSection title={formatMessage({ defaultMessage: 'Matcha fält' })}>
      <div role="status" aria-live="polite" aria-atomic="true">
        {unmappedRequiredCount > 0 ? (
          <span className="mb-4 font-bold text-red-500">
            <FormattedMessage
              defaultMessage="{count, plural, one {# obligatoriskt fält} other {# obligatoriska fält}} kvar att matcha"
              values={{ count: unmappedRequiredCount }}
            />
          </span>
        ) : (
          <span className="mb-4">
            <FormattedMessage defaultMessage="Alla obligatoriska fält är matchade" />
          </span>
        )}
      </div>
      {importMeta.file.importColumns.some(
        (col) => !mappedColumns[col.header],
      ) && (
        <div className="mb-4 flex flex-col gap-2">
          <b>
            <FormattedMessage defaultMessage="För att förhindra misstag måste alla oavända fält sättas till 'Använd inte'" />
          </b>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSetUnassignedToDoNotUse}
          >
            <FormattedMessage defaultMessage="Sätt omatchade fält till 'Använd inte'" />
          </Button>
        </div>
      )}
      {importMeta.file.importColumns.map((field, index) => (
        <FormElement
          key={field.header}
          className="items-center border-b border-background-200 pb-2"
          label={
            <div className="flex flex-col">
              <span className="font-bold">{field.header}</span>
              <span className={cn(field.value ? '' : 'italic')}>
                {field.value ? (
                  field.value
                ) : (
                  <FormattedMessage defaultMessage="Fält tomt" />
                )}
              </span>
            </div>
          }
          input={
            <div className="flex flex-col gap-1">
              <Dropdown
                key={`${field.header}-${mappedColumns[field.header] || 'empty'}`}
                id={field.header}
                value={checkAndSetDropdownValue(field)}
                placeholder={formatMessage({ defaultMessage: 'Välj fält' })}
                options={toDropdownOptions.map((option) =>
                  getFieldOptionWithMappingStatus(option),
                )}
                onValueChange={(value) => {
                  if (!value) {
                    return;
                  }
                  const setValue =
                    value.value === DO_NOT_USE_ID
                      ? createIndexedKey(DO_NOT_USE_ID, index)
                      : (value.value as string);
                  changeOption(field.header, setValue ?? '');
                }}
                preventReselect={true}
                optionsContainerMaxHeight="300px"
              />
              {suggestedHeaders.has(field.header) &&
                mappedColumns[field.header] && (
                  <span className="text-sm text-text-700">
                    <FormattedMessage defaultMessage="Förslag från tidigare import" />
                  </span>
                )}
            </div>
          }
        />
      ))}
    </FormSection>
  );
};
